import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../../../components/recharts/functions";
import { ArrowLeft, Volume } from "lucide-react";
import { ReMixChart } from "../../../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../../../models/chart_model";
import { renderHourTick } from "../../../../../components/recharts/components";
import { useDispatch, useSelector } from "react-redux";
import FilterMenu from "../../../../../components/FilterMenu";
import { setFilterWidth } from "../../../../../store/state/FilterWidth";
import { R_CHART_TYPES } from "../../../Utility/const";
let X_AXIS = [
  {
    label: "Time Slot",
    key: "time_slot__slot",
    values: ["block", "date"],
    isTime: true,
  },
  {
    label: "Generator",
    key: "generator",
    values: ["generator", "date"],
    isTime: false,
  },
];
let Y_AXIS = ["Cumulative", "Mean"];

const chartSuffixesVolume = [
  {
    suffix: "total_capacity",
    displayName: "Declared Capacity",
    stackSuffix: "total_capacity",
    chartType: ChartType.Bar,
    stroke: "rgba(255, 171, 121, 1)",
    legendColor: "rgba(255, 171, 121, 1)",
    unit: "MW",
    yAxisId: "left",
  },
  {
    suffix: "must_run_value",
    displayName: "Must Run",
    stackSuffix: "must_run_value",
    chartType: ChartType.Bar,
    stroke: "rgba(132, 187, 199, 1)",
    legendColor: "rgba(132, 187, 199, 1)",
    unit: "MW",
    yAxisId: "left",
  },
];
const chartSuffixesRate = [
  {
    suffix: "vc",
    displayName: "Variable Charge",
    stackSuffix: "vc",
    chartType: ChartType.Line,
    unit: "Rs/KWh",
    yAxisId: "right",
  },
  {
    suffix: "margin_buy",
    displayName: "Buy Margin",
    stackSuffix: "margin_buy",
    chartType: ChartType.Line,
    unit: "Rs/KWh",
    yAxisId: "right",
  },
  {
    suffix: "margin_conservative_sell",
    displayName: "Sell Margin Conservative",
    stackSuffix: "margin_conservative_sell",
    chartType: ChartType.Line,
    unit: "Rs/KWh",
    yAxisId: "right",
  },
  {
    suffix: "margin_aggressive_sell",
    displayName: "Sell Margin Aggressive",
    stackSuffix: "margin_aggressive_sell",
    chartType: ChartType.Line,
    unit: "Rs/KWh",
    yAxisId: "right",
  },
];

export default function ModChart({
  selectedBlock,
  selectedGenerator,
  selectedGeneratorType,
  selectedOwnership,
  data,
}: {
  selectedGeneratorType: any[];
  selectedOwnership: any[];
  selectedGenerator: any[];
  selectedBlock: any[];
  data: any[];
}) {
  const [open, setOpen] = useState(true);
  const [fullbtn, setFullBtn] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedSuffixRate, setSelectedSuffixRate] = useState<any[]>([
    chartSuffixesRate[0],
    {
      suffix: "demand_mw",
      displayName: "Demand",
      stackSuffix: "demand_mw",
      chartType: ChartType.Line,
      stroke: "rgba(4, 4, 98, 1)",
      legendColor: "rgba(4, 4, 98, 1)",
      unit: "MW",
      yAxisId: "left",
    },
  ]);
  const [selectedSuffixVolume, setSelectedSuffixVolume] = useState<any[]>([
    chartSuffixesVolume[0],
  ]);
  const [selectedInterval, setSelectedInterval] = useState(X_AXIS[0]);
  const [selectedYaxis, setSelectedYaxis] = useState(Y_AXIS[1]);
  // const [selectedVolume, setSelectedVolume] = useState(VOLUME[0].key);
  // const [selectedRate, setSelectedRate] = useState([]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[0]);
  const preSelectedDate = useSelector(
    (state: any) => state.modRuvnlDate.selectedDate
  );
  // const modSelectedSession = useSelector(
  //   (state: any) => state.modRuvnlSession.modSession
  // );
  const filteredChartTypes = R_CHART_TYPES.filter(
    (item) => item.label !== "Stacked Bar"
  );
  function _handleSuffixVolumeChange(index: number) {
    if (selectedSuffixVolume.length > 1) {
      setSelectedSuffixVolume([chartSuffixesVolume[index]]);
    } else {
      if (selectedSuffixVolume.includes(chartSuffixesVolume[index])) {
        return;
      }
      setSelectedSuffixVolume([
        ...selectedSuffixVolume,
        chartSuffixesVolume[index],
      ]);
    }

    setSelectedSuffixVolume((prev) =>
      prev.sort(
        (a, b) =>
          chartSuffixesVolume.indexOf(a) - chartSuffixesVolume.indexOf(b)
      )
    );
  }
  function _handleSuffixRateChange(index: number) {
    if (selectedSuffixRate.length > 2) {
      setSelectedSuffixRate([
        {
          suffix: "demand_mw",
          displayName: "Demand",
          stackSuffix: "demand_mw",
          chartType: ChartType.Line,
          stroke: "rgba(4, 4, 98, 1)",
          legendColor: "rgba(4, 4, 98, 1)",
          unit: "MW",
          yAxisId: "left",
        },
        chartSuffixesRate[index],
      ]);
    } else {
      if (selectedSuffixRate.includes(chartSuffixesRate[index])) {
        return;
      }
      setSelectedSuffixRate([...selectedSuffixRate, chartSuffixesRate[index]]);
    }

    setSelectedSuffixRate((prev) =>
      prev.sort(
        (a, b) => chartSuffixesRate.indexOf(a) - chartSuffixesRate.indexOf(b)
      )
    );
  }

  useEffect(() => {
    let filteredData = data.map((item) => ({
      ...item,
      date: preSelectedDate,
    }));

    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock
          .map((b) => Number(b.value))
          .includes(Number(item.time_slot__slot))
      );
    }
    if (selectedGenerator.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedGenerator
          .map((b) => String(b.value))
          .includes(String(item.generator))
      );
    }
    if (selectedGeneratorType.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedGeneratorType
          .map((b) => String(b.value))
          .includes(String(item.generator_type))
      );
    }
    if (selectedOwnership.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedOwnership
          .map((b) => String(b.value))
          .includes(String(item.ownership))
      );
    }
    // filteredData = filteredData.filter(d => d.total_capacity > 0);
    function getKeys() {
      if (selectedInterval.key === "time_slot__slot") {
        return ["date", "time_slot__slot"];
      } else if (selectedInterval.key === "generator") {
        return ["date", "generator"];
      } else {
        return [selectedInterval.values[0]];
      }
    }
    const par: ChartDataParams = {
      keys: getKeys(),
      tableKeys: ["date", "time_slot__slot", "generator"],
      groupBy: [""],
      volumeKey: ``,
      aggregationKeys: [
        "vc",
        "total_capacity",
        "must_run_value",
        "margin_buy",
        "margin_aggressive_sell",
        "margin_conservative_sell",
      ],
      average: true,
      averageKeys: selectedInterval.isTime
        ? [
            "vc",
            "demand_mw",
            "margin_buy",
            "margin_aggressive_sell",
            "margin_conservative_sell",
          ]
        : [
            "vc",
            "total_capacity",
            "must_run_value",
            "margin_buy",
            "margin_aggressive_sell",
            "margin_conservative_sell",
          ],
      averageDuplicationKeys: ["date", "time_slot__slot", "generator"],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [
    data,
    preSelectedDate,
    selectedBlock,
    selectedGenerator,
    selectedGeneratorType,
    selectedInterval.isTime,
    selectedInterval.key,
    selectedInterval.values,
    selectedOwnership,
  ]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: ChartType,
    unit: string,
    stroke: string,
    legendColor: string,
    yAxisId: string
  ) => {
    return {
      dataKey: `${key}_${suffix}`,
      name: `${key.replace("_", " ").replace("undefined", "")} ${displayName}`,
      type: [
        "vc",
        "demand_mw",
        "margin_buy",
        "margin_conservative_sell",
        "margin_aggressive_sell",
      ].includes(suffix)
        ? ChartType.Line
        : selectedChartType.value,
      unit: unit,
      stroke: stroke,
      legendColor: legendColor,
      yAxisId: yAxisId,
      stackId:
        selectedChartType.stackId !== undefined ? stackSuffix : undefined,
    };
  };

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        if (selectedInterval.isTime) {
          return "time_slot__slot";
        } else {
          return selectedInterval.values[0];
        }
      }
      return "time_slot__slot";
    } else {
      return selectedInterval.key;
    }
  }, [
    selectedBlock.length,
    selectedInterval.isTime,
    selectedInterval.key,
    selectedInterval.values,
  ]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  function secondDataKey() {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        return "date";
      }
      return "date";
    } else {
      return "date";
    }
  }
  function getLegends() {
    return [...selectedSuffixRate, ...selectedSuffixVolume].flatMap(
      ({
        suffix,
        displayName,
        stackSuffix,
        chartType,
        unit,
        stroke,
        legendColor,
        yAxisId,
      }) =>
        chartKeys
          .map((key, index) =>
            createChartObject(
              key,
              index,
              suffix,
              displayName,
              stackSuffix,
              chartType,
              unit,
              stroke,
              legendColor,
              yAxisId
            )
          )
          .filter(
            (item) =>
              !(
                selectedInterval.key === "generator" &&
                item.dataKey === "undefined_demand_mw"
              )
          )
    );
  }

  const handleWidthChange = useMemo(
    () => (width: number) => {
      if (open) {
        const roundedWidth = Math.ceil(width + 30);
        const pixelValue = `${roundedWidth}px`;
        const pageWidth = `calc(100% - ${pixelValue} - 35px)`;
        dispatch(setFilterWidth(pageWidth));
      }
    },
    [dispatch, open]
  );

  // Create a debounced version that we'll use in the observer
  const debouncedHandleWidthChange = useMemo(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    return (...args: Parameters<typeof handleWidthChange>) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        handleWidthChange(...args);
        timeoutId = null;
      }, 100);
    };
  }, [handleWidthChange]);

  // Set up ResizeObserver with debounce
  useEffect(() => {
    if (!containerRef.current) return;
    const currentContainer = containerRef.current;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        debouncedHandleWidthChange(entry.contentRect.width);
      }
    });

    resizeObserver.observe(currentContainer);

    return () => {
      resizeObserver.unobserve(currentContainer);
    };
  }, [debouncedHandleWidthChange]);

  // Handle open/close state changes
  useEffect(() => {
    if (!open) {
      const pageWidth = `calc(100% - 70px)`;
      dispatch(setFilterWidth(pageWidth));
    }
    // When opening, the ResizeObserver will handle the width change
  }, [open, dispatch]);
  return (
    <>
      <div className="flex max-h-[calc(100vh-300px)]">
        <div className="h-100 border border-[#718EBF1C] rounded-lg shadow-[rgba(149,157,165,0.2)_0px_8px_24px] ">
          <div className="hide-btn-outer">
            {/* <button
              style={{
                width: fullbtn ? "100px" : "30px",
                overflow: "hidden",
                height: "30px",
                [open ? "right" : "left"]: open ? "-7px" : "20px",
              }}
              onMouseOver={() => setFullBtn(true)}
              onMouseLeave={() => setFullBtn(false)}
              onClick={() => setOpen(!open)}
              className="hide-btn flex items-center justify-between gap-1 transition-all text-nowrap duration-700 ease-in-out"
            >
              <ArrowLeft
                size={18}
                className={`text-primary transition-transform duration-300 ${
                  !open ? "rotate-180" : ""
                }`}
              />
              {fullbtn && (
                <>
                  {open ? <span>Hide Filters</span> : <span>Show Filters</span>}
                </>
              )}
            </button> */}

            <div
              ref={containerRef}
              className={`transition-all  duration-700 ease-in-out bg-white m-[0.8rem] me-1 rounded-2xl`}
              style={{
                width: open ? "auto" : "0px",
                height: "calc(100vh - 328px)",
                transition: "all 0.3s ease-in-out, opacity 0.3s ease-in-out",
                padding: open ? "0px" : "12px",
                paddingRight: open ? "1rem" : "12px",
              }}
            >
              <div
                className="rounded-2xl"
                style={{
                  height: "calc(100vh - 328px)",
                  overflowY: open ? "auto" : "hidden",
                }}
              >
                <div
                  style={{
                    opacity: open ? 1 : 0,
                  }}
                  className="transition-all duration-700 ease-in-out"
                >
                  <div className="side-filters-container text-nowrap">
                    <FilterMenu
                      title="X Axis"
                      items={X_AXIS.map((item) => item.label)}
                      selectedItems={[selectedInterval.label]}
                      onSelect={(item, index) => {
                        setSelectedInterval(X_AXIS[index]);
                      }}
                    />
                    {/* <FilterMenu
                      title="Y Axis"
                      items={Y_AXIS.map((item) => item)}
                      selectedItems={[selectedYaxis]}
                      onSelect={(item, index) => {
                        setSelectedYaxis(Y_AXIS[index]);
                      }}
                    /> */}
                    <FilterMenu
                      title="Volume (MW)"
                      items={chartSuffixesVolume.map(
                        (item) => item.displayName
                      )}
                      selectedItems={selectedSuffixVolume.map(
                        (item) => item.displayName
                      )}
                      onSelect={(item, index) => {
                        _handleSuffixVolumeChange(index);
                      }}
                    />
                    {/* <FilterMenu
            title="Type"
            items={TYPE}
            selectedItems={selectedSuffix.map((item) => item.displayName)}
            onSelect={(item, index) => {
              _handleSuffixChange(index);
            }}
          /> */}
                    <FilterMenu
                      title="Rate (Rs/KWh)"
                      items={chartSuffixesRate.map((item) => item.displayName)}
                      selectedItems={selectedSuffixRate.map(
                        (item) => item.displayName
                      )}
                      onSelect={(item, index) => {
                        _handleSuffixRateChange(index);
                      }}
                    />

                    <FilterMenu
                      title="Chart Type"
                      items={filteredChartTypes.map((item) => item.label)}
                      selectedItems={[selectedChartType.label]}
                      onSelect={(item, index) => {
                        setSelectedChartType(filteredChartTypes[index]); // ✅ use filtered array
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex-1 h-100 ml-4 shadow-[rgba(149,157,165,0.2)_0px_8px_24px]">
          <div className="dash-card col-span-1 md:col-span-6 border border-[#718EBF1C] !bg-[] !shadow-none !h-full w-full">
            <ReMixChart
              chartHeight="calc(100% - 50px)"
              data={chartData}
              showBrush={true}
              brushStart={BrushStart.Start}
              isTimeSlot={selectedInterval.isTime ? true : false}
              onlyTitle={true}
              legends={getLegends()}
              yAxisLabel="MW"
              secondYAxisLabel="Rs/KWh"
              nameGap={40}
              xDataKey={xDataKey}
              top={10} // Top margin for grid
              bottom={30} // Bottom margin for grid
              xTick={
                selectedInterval.isTime && selectedBlock.length === 0
                  ? (value: any, index: any) => {
                      return renderHourTick(value, 10);
                    }
                  : undefined
              }
              secondXDataKey={secondDataKey()}
              showToolbox={false}
            />
          </div>
        </div>
      </div>
    </>
  );
}
